import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, BookOpen, Target, Trophy, TrendingUp, Loader2, Sparkles, Crown } from "lucide-react";
import { getDashboard } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InviteCard } from "@/components/InviteCard";
import { ReferralLeaderboard } from "@/components/ReferralLeaderboard";
import { StudyGroupCard } from "@/components/StudyGroupCard";
import { PreferencesCard } from "@/components/PreferencesCard";
import { AdminSalesCard } from "@/components/AdminSalesCard";
import { AdminSignupsCard } from "@/components/AdminSignupsCard";
import { AdminTestimonialsCard } from "@/components/AdminTestimonialsCard";
import { AdminLaunchEmailerCard } from "@/components/AdminLaunchEmailerCard";
import { AdminBetaCodesCard } from "@/components/AdminBetaCodesCard";
import { ShareHubCard } from "@/components/ShareHubCard";
import { AvAnalyticsCard } from "@/components/AvAnalyticsCard";
import { SecurityCard } from "@/components/SecurityCard";
import { LoginHistoryCard } from "@/components/LoginHistoryCard";
import { AdminWeeklyRecapCard } from "@/components/AdminWeeklyRecapCard";
import { GoogleReviewNudge } from "@/components/GoogleReviewNudge";
import { BadgesCard } from "@/components/BadgesCard";

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
    <div className={`grid place-items-center h-10 w-10 rounded-xl mb-3 ${accent ? "bg-accent/15 text-accent" : "bg-secondary text-primary"}`}>
      <Icon className="h-5 w-5" />
    </div>
    <p className="font-serif text-3xl font-bold">{value}</p>
    <p className="text-sm text-muted-foreground">{label}</p>
  </div>
);

export const Dashboard = ({ onOpenLecture, onUpgrade, onQuickStudy }) => {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { getDashboard().then(setData).catch(() => setData(false)); }, []);

  if (data === null) {
    return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }
  if (data === false) return <div className="text-center py-24 text-muted-foreground">Could not load your dashboard.</div>;

  const masteryPct = data.concepts_total ? Math.round((data.concepts_mastered / data.concepts_total) * 100) : 0;

  // last 14 days streak calendar
  const days = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().slice(0, 10);
    days.push({ iso, active: (data.active_days || []).includes(iso), label: d.toLocaleDateString(undefined, { weekday: "narrow" }) });
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Your Progress</span>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">Hi {user?.name || "there"} 👋</h1>
        </div>
        <button
          data-testid="dashboard-quickstudy-btn"
          onClick={onQuickStudy}
          className="flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
        >
          <Sparkles className="h-4 w-4" /> I have 10 minutes
        </button>
      </div>

      {/* Streak hero */}
      <div className="rounded-3xl border border-border bg-card shadow-sm p-6 sm:p-8 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="grid place-items-center h-16 w-16 rounded-2xl bg-accent/15 text-accent">
              <Flame className="h-8 w-8" />
            </div>
            <div>
              <p data-testid="streak-count" className="font-serif text-4xl font-bold">{data.current_streak} day{data.current_streak !== 1 && "s"}</p>
              <p className="text-sm text-muted-foreground">Current study streak · best {data.longest_streak}</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {days.map((d, i) => (
              <div key={i} className="text-center">
                <div
                  title={d.iso}
                  className={`h-8 w-8 rounded-lg ${d.active ? "bg-accent" : "bg-secondary"}`}
                />
                <span className="text-[10px] text-muted-foreground">{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat icon={BookOpen} label="Lectures created" value={data.total_lectures} />
        <Stat icon={Trophy} label="Quizzes taken" value={data.quizzes_taken} accent />
        <Stat icon={TrendingUp} label="Avg quiz score" value={`${data.avg_score}%`} />
        <Stat icon={Target} label="Concepts mastered" value={`${data.concepts_mastered}/${data.concepts_total}`} accent />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Mastery ring */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm text-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Overall Mastery</span>
          <div className="relative inline-grid place-items-center my-4">
            <svg width="140" height="140" viewBox="0 0 140 140" className="-rotate-90">
              <circle cx="70" cy="70" r="60" fill="none" stroke="hsl(var(--secondary))" strokeWidth="12" />
              <motion.circle
                cx="70" cy="70" r="60" fill="none" stroke="hsl(var(--accent))" strokeWidth="12" strokeLinecap="round"
                strokeDasharray={2 * Math.PI * 60}
                initial={{ strokeDashoffset: 2 * Math.PI * 60 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 60 * (1 - masteryPct / 100) }}
                transition={{ duration: 1 }}
              />
            </svg>
            <span className="absolute font-serif text-3xl font-bold">{masteryPct}%</span>
          </div>
          <p className="text-sm text-muted-foreground">{data.concepts_mastered} of {data.concepts_total} concepts mastered</p>
        </div>

        {/* Weak concepts */}
        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Needs Attention</span>
          </div>
          {data.weak_concepts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">No weak spots yet — take a few quizzes to see targeted areas here.</p>
          ) : (
            <div className="space-y-2">
              {data.weak_concepts.map((w, i) => (
                <button
                  key={i}
                  onClick={() => onOpenLecture && onOpenLecture(w.lecture_id)}
                  className="w-full flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5 text-left hover:border-accent/50 transition"
                >
                  <div>
                    <p className="text-sm font-medium">{w.concept}</p>
                    <p className="text-xs text-muted-foreground">{w.lecture_title}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-destructive/10 text-destructive px-2.5 py-1 text-xs font-bold">{w.best_pct}%</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.plan !== "pro" && (() => {
        const returning = ["canceled", "revoked"].includes(user?.subscription_status);
        return (
        <div className="mt-6 rounded-2xl border-2 border-accent/30 bg-accent/5 p-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-accent" />
            <div>
              <p className="font-serif text-xl font-semibold">{returning ? "Welcome back!" : "Unlock your full potential"}</p>
              <p className="text-sm text-muted-foreground">
                {returning
                  ? "Your Pro access ended. Resubscribe to pick up right where you left off — your lectures and progress are saved."
                  : "Go Pro for unlimited lectures, voice narration, the tutor, Focus Mode & quick study."}
              </p>
            </div>
          </div>
          <button
            data-testid={returning ? "resubscribe-btn" : "dashboard-upgrade-btn"}
            onClick={onUpgrade}
            className="rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:opacity-90 transition"
          >
            {returning ? "Resubscribe" : "Upgrade — from $3.75/mo"}
          </button>
        </div>
        );
      })()}

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <InviteCard />
        <ReferralLeaderboard />
      </div>
      <div className="mt-6">
        <StudyGroupCard />
      </div>
      <div className="mt-6">
        <BadgesCard dashboard={data} />
      </div>
      <div className="mt-6">
        <SecurityCard />
      </div>
      <div className="mt-6">
        <LoginHistoryCard />
      </div>
      <div className="mt-6">
        <GoogleReviewNudge />
      </div>
      <div className="mt-6">
        <PreferencesCard />
      </div>
      <div className="mt-6">
        <AdminWeeklyRecapCard role={user?.role} />
      </div>
      <div className="mt-6">
        <ShareHubCard role={user?.role} />
      </div>
      <div className="mt-6">
        <AdminTestimonialsCard role={user?.role} />
      </div>
      <div className="mt-6">
        <AdminLaunchEmailerCard role={user?.role} />
      </div>
      <div className="mt-6">
        <AdminBetaCodesCard role={user?.role} />
      </div>
      <div className="mt-6">
        <AdminSignupsCard />
      </div>
      <div className="mt-6">
        <AdminSalesCard />
      </div>
      <div className="mt-6">
        <AvAnalyticsCard />
      </div>
    </div>
  );
};
